import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'react-qr-code';
import { Check, Copy, Link2, Loader2, Plus, QrCode, RefreshCw, Share2, UserRoundX, Users } from 'lucide-react';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Checkbox } from '../../ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Skeleton } from '../../ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { useToast } from '../../ui/use-toast';
import { errorCodeOf } from '../../../services/api-error';
import {
  roundDelegationsService,
  type RoundDelegation,
  type RoundDelegationKind,
  type RoundDelegationsPage,
} from '../../../services/round-delegations';
import { helperBasePath } from './storage';

const QR_SIZE = 224;

interface ShareRoundDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  competitionSlug: string;
  roundId: string;
  roundName: string;
}

/**
 * What the dialog is showing: the helpers already given a link, picking the
 * athletes for a new one (or for an existing one), or the link to hand over.
 */
type Step =
  | { kind: 'list' }
  | { kind: 'choose' }
  | { kind: 'pick'; delegationId: string | null; selected: string[] }
  | { kind: 'share'; url: string };

/**
 * The "share" button's dialog on a round's card: a team manager or an athlete
 * handing some cards of this round to somebody else's phone, which needs no
 * account (RoundDelegation on the server).
 *
 * A team manager picks which of their athletes the helper gets; an athlete
 * hands over their own card. Then the link: as a QR code for the phone next to
 * them, or as a link to send. **One phone per link** — the link is spent when
 * somebody accepts it — so a second helper is a second link, and a helper who
 * lost the page gets a new one ("Nuovo link") that keeps their name and
 * athletes. The link's token is only ever in the answer that made it: a link
 * nobody took yet is replaced, never shown again.
 */
export function ShareRoundDialog({ open, onOpenChange, competitionSlug, roundId, roundName }: ShareRoundDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();

  const [page, setPage] = useState<RoundDelegationsPage | null>(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<Step>({ kind: 'list' });
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmingRevoke, setConfirmingRevoke] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await roundDelegationsService.list(competitionSlug, roundId);
      setPage(data);
      return data;
    } catch (error) {
      toast({
        title: t('errors.loadFailed'),
        description: error instanceof Error ? error.message : undefined,
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [competitionSlug, roundId, t, toast]);

  // Fresh each time it opens: a helper may have accepted in the meantime.
  useEffect(() => {
    if (!open) return;
    setStep({ kind: 'list' });
    setConfirmingRevoke(null);
    load().then((data) => {
      if (data && data.delegations.length === 0) startNew(data);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per opening
  }, [open]);

  const linkFor = (token: string) =>
    `${window.location.origin}${helperBasePath({ competitionSlug, roundId, token })}`;

  /** A new helper: the athlete has nothing to pick, a team manager does. */
  function startNew(data: RoundDelegationsPage | null = page) {
    if (!data) return;
    const kinds = data.delegableKinds;
    if (kinds.length > 1) setStep({ kind: 'choose' });
    else if (kinds[0] === 'team') setStep({ kind: 'pick', delegationId: null, selected: [] });
    else if (kinds[0] === 'athlete') create('athlete', []);
  }

  const failed = (error: unknown) => {
    const code = errorCodeOf(error);
    toast({
      title: t('roundHelp.share.failed'),
      description: code === 'dpa_required' ? t('roundHelp.share.dpaRequired') : error instanceof Error ? error.message : undefined,
      variant: 'destructive',
    });
  };

  async function create(kind: RoundDelegationKind, participantIds: string[]) {
    setBusy('create');
    try {
      const made = await roundDelegationsService.create(competitionSlug, roundId, kind, participantIds);
      if (made.token) setStep({ kind: 'share', url: linkFor(made.token) });
      load();
    } catch (error) {
      failed(error);
    } finally {
      setBusy(null);
    }
  }

  const saveAthletes = async (delegationId: string, participantIds: string[]) => {
    setBusy('create');
    try {
      await roundDelegationsService.updateParticipants(competitionSlug, roundId, delegationId, participantIds);
      await load();
      setStep({ kind: 'list' });
    } catch (error) {
      failed(error);
    } finally {
      setBusy(null);
    }
  };

  const reissue = async (delegation: RoundDelegation) => {
    setBusy(`reissue-${delegation.id}`);
    try {
      const fresh = await roundDelegationsService.reissue(competitionSlug, roundId, delegation.id);
      if (fresh.token) setStep({ kind: 'share', url: linkFor(fresh.token) });
      load();
    } catch (error) {
      failed(error);
    } finally {
      setBusy(null);
    }
  };

  const revoke = async (delegation: RoundDelegation) => {
    setBusy(`revoke-${delegation.id}`);
    try {
      await roundDelegationsService.revoke(competitionSlug, roundId, delegation.id);
      setConfirmingRevoke(null);
      await load();
    } catch (error) {
      failed(error);
    } finally {
      setBusy(null);
    }
  };

  const shareLink = async (url: string) => {
    try {
      if (navigator.share) {
        await navigator.share({ title: t('roundHelp.share.shareTitle', { round: roundName }), url });
        return;
      }
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // The share sheet dismissed, or no clipboard: the link is on screen to copy.
    }
  };

  const copyLink = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // No clipboard: the link is selectable in the field.
    }
  };

  const statusLabel = (delegation: RoundDelegation) => t(`roundHelp.share.status.${delegation.status}`);

  const renderList = () => {
    const delegations = page?.delegations ?? [];
    return (
      <div className="space-y-3" data-testid="helpers-list">
        {delegations.length === 0 && (
          <p className="text-sm text-muted-foreground">{t('roundHelp.share.noHelpers')}</p>
        )}
        {delegations.map((delegation) => (
          <div key={delegation.id} className="rounded-md border p-3" data-testid="helper-row">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">
                  {delegation.helper_name ?? t('roundHelp.share.waiting')}
                </p>
                {delegation.kind === 'team' && (
                  <p className="text-xs text-muted-foreground">
                    {delegation.participants.map((participant) => participant.full_name).join(', ')}
                  </p>
                )}
              </div>
              <Badge variant={delegation.status === 'active' ? 'default' : 'outline'} className="shrink-0">
                {statusLabel(delegation)}
              </Badge>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {delegation.kind === 'team' && (
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1.5"
                  onClick={() =>
                    setStep({
                      kind: 'pick',
                      delegationId: delegation.id,
                      selected: delegation.participants.map((participant) => participant.id),
                    })
                  }
                  data-testid="helper-athletes"
                >
                  <Users className="size-3.5" aria-hidden />
                  {t('roundHelp.share.athletes')}
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                className="gap-1.5"
                disabled={busy === `reissue-${delegation.id}`}
                onClick={() => reissue(delegation)}
                data-testid="helper-reissue"
              >
                <RefreshCw className="size-3.5" aria-hidden />
                {t('roundHelp.share.reissue')}
              </Button>
              <Button
                size="sm"
                variant={confirmingRevoke === delegation.id ? 'destructive' : 'ghost'}
                className="gap-1.5"
                disabled={busy === `revoke-${delegation.id}`}
                onClick={() =>
                  confirmingRevoke === delegation.id ? revoke(delegation) : setConfirmingRevoke(delegation.id)
                }
                data-testid="helper-revoke"
              >
                <UserRoundX className="size-3.5" aria-hidden />
                {t(confirmingRevoke === delegation.id ? 'roundHelp.share.revokeConfirm' : 'roundHelp.share.revoke')}
              </Button>
            </div>
          </div>
        ))}
        <Button className="w-full gap-1.5" onClick={() => startNew()} disabled={busy === 'create'} data-testid="add-helper">
          {busy === 'create' ? <Loader2 className="size-4 animate-spin" aria-hidden /> : <Plus className="size-4" aria-hidden />}
          {t('roundHelp.share.addHelper')}
        </Button>
      </div>
    );
  };

  const renderChoose = () => (
    <div className="space-y-2" data-testid="share-choose">
      <p className="text-sm text-muted-foreground">{t('roundHelp.share.chooseKind')}</p>
      <Button
        variant="outline"
        className="w-full"
        onClick={() => setStep({ kind: 'pick', delegationId: null, selected: [] })}
        data-testid="share-kind-team"
      >
        {t('roundHelp.share.kindTeam')}
      </Button>
      <Button
        variant="outline"
        className="w-full"
        onClick={() => create('athlete', [])}
        disabled={busy === 'create'}
        data-testid="share-kind-athlete"
      >
        {t('roundHelp.share.kindAthlete')}
      </Button>
    </div>
  );

  const renderPick = (current: Extract<Step, { kind: 'pick' }>) => {
    const candidates = page?.delegableParticipants ?? [];
    const toggle = (id: string, checked: boolean) =>
      setStep({
        ...current,
        selected: checked ? [...current.selected, id] : current.selected.filter((selected) => selected !== id),
      });
    const allChecked = candidates.length > 0 && current.selected.length === candidates.length;

    return (
      <div className="space-y-3" data-testid="share-pick">
        <p className="text-sm text-muted-foreground">{t('roundHelp.share.pickAthletes')}</p>
        {candidates.length > 1 && (
          <label className="flex items-center gap-2 text-sm font-medium">
            <Checkbox
              checked={allChecked}
              onCheckedChange={(checked) =>
                setStep({ ...current, selected: checked ? candidates.map((candidate) => candidate.id) : [] })
              }
            />
            {t('roundHelp.share.allAthletes')}
          </label>
        )}
        <div className="max-h-64 space-y-1 overflow-y-auto">
          {candidates.map((candidate) => (
            <label
              key={candidate.id}
              className="flex items-center gap-2 rounded-md px-1 py-1.5 text-sm hover:bg-muted/50"
              data-testid="share-athlete"
            >
              <Checkbox
                checked={current.selected.includes(candidate.id)}
                onCheckedChange={(checked) => toggle(candidate.id, checked === true)}
              />
              {candidate.full_name}
            </label>
          ))}
        </div>
        <div className="flex gap-2">
          {(page?.delegations.length ?? 0) > 0 && (
            <Button variant="ghost" onClick={() => setStep({ kind: 'list' })}>
              {t('common.cancel')}
            </Button>
          )}
          <Button
            className="flex-1"
            disabled={current.selected.length === 0 || busy === 'create'}
            onClick={() =>
              current.delegationId
                ? saveAthletes(current.delegationId, current.selected)
                : create('team', current.selected)
            }
            data-testid="share-confirm"
          >
            {busy === 'create' && <Loader2 className="mr-2 size-4 animate-spin" aria-hidden />}
            {t(current.delegationId ? 'roundHelp.share.saveAthletes' : 'roundHelp.share.makeLink')}
          </Button>
        </div>
      </div>
    );
  };

  const renderShare = (url: string) => (
    <div className="space-y-3" data-testid="share-link">
      <p className="text-sm text-muted-foreground">{t('roundHelp.share.howToShare')}</p>
      <Tabs defaultValue="qr">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="qr" className="gap-1.5">
            <QrCode className="size-4" aria-hidden />
            {t('roundHelp.share.qr')}
          </TabsTrigger>
          <TabsTrigger value="link" className="gap-1.5">
            <Link2 className="size-4" aria-hidden />
            {t('roundHelp.share.link')}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="qr" className="flex flex-col items-center gap-2 pt-2">
          {/* White behind the code whatever the theme: a phone's camera reads
              dark on light. */}
          <div className="rounded-lg bg-white p-3" data-testid="share-qr">
            <QRCode value={url} size={QR_SIZE} />
          </div>
          <p className="text-center text-xs text-muted-foreground">{t('roundHelp.share.qrHint')}</p>
        </TabsContent>
        <TabsContent value="link" className="space-y-2 pt-2">
          <div className="flex items-center gap-2">
            <Input value={url} readOnly onFocus={(event) => event.currentTarget.select()} className="font-mono text-xs" />
            <Button variant="secondary" size="icon" onClick={() => copyLink(url)} title={t('roundHelp.share.copy')}>
              {copied ? <Check className="size-4" aria-hidden /> : <Copy className="size-4" aria-hidden />}
            </Button>
          </div>
          <Button className="w-full gap-1.5" onClick={() => shareLink(url)} data-testid="share-native">
            <Share2 className="size-4" aria-hidden />
            {t('roundHelp.share.send')}
          </Button>
        </TabsContent>
      </Tabs>
      <p className="text-xs text-muted-foreground">{t('roundHelp.share.onePhone')}</p>
      <Button variant="outline" className="w-full" onClick={() => setStep({ kind: 'list' })} data-testid="share-done">
        {t('roundHelp.share.done')}
      </Button>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('roundHelp.share.title', { round: roundName })}</DialogTitle>
          <DialogDescription>{t('roundHelp.share.description')}</DialogDescription>
        </DialogHeader>

        {loading && !page ? (
          <div className="space-y-2">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : step.kind === 'choose' ? (
          renderChoose()
        ) : step.kind === 'pick' ? (
          renderPick(step)
        ) : step.kind === 'share' ? (
          renderShare(step.url)
        ) : (
          renderList()
        )}
      </DialogContent>
    </Dialog>
  );
}
